import { isLiveChatResponse } from "@alme/api-ts/Utils";
import { ConfigurationKeyValuePair } from "@alme/types/response/ConfigurationResponse";
import { LiveChatNotificationResponseNext } from "@alme/types/response/LiveChatNotificationResponse";
import { Dispatch, SetStateAction, useEffect } from "react";
import { AlmeApiUtils } from "../api/AlmeApiUtils";
import { AlmeAppState } from "../components/AlmeApp/AlmeAppState";
import { loadAlmeHistory } from "../components/AlmeApp/AlmeAppUtils";
import { AlmeResponseNext } from '@alme/types/response/AlmeResponse';
import defaultLogger from '../Logger';
import { AlmeEnvironmentConfig } from "../api/AlmeEnvironmentConfig";
import { AlmeSessionUtils } from "../api/AlmeSessionUtils";

export function useAlmeInitialization(apiUtils: React.MutableRefObject<AlmeApiUtils>,
    almeState: AlmeAppState,
    isInitialized: React.MutableRefObject<boolean>,
    setAlmeStateWrapper: (state: AlmeAppState) => void,
    personaMapped: boolean,
    scrollToResponse: React.MutableRefObject<boolean>,
    shouldScrollOnNav?: boolean,
    setShouldDisplayTimeout?: Dispatch<SetStateAction<boolean>>,
    isIvaProLaunch?: React.MutableRefObject<boolean>,
    almeSessionUtils?: React.MutableRefObject<AlmeSessionUtils | undefined>): void {
    useEffect(() => {
        /**
         * Alme app initialization sequence.
         * 1. Send a GetConfiguration Alme request convert from Key/value pair to property/value and save the results in the almeState.config.
         * 2. Send a GetHistory Alme request and if history exists:
         *    - Disable all auto navigation by setting response.navUrl.AutoNavigationType = 'None'.
         *    - Save the history in the almeState.history.
         * 3. Else if no history exists Send a "WelcomeUser" AppEvent request and save the response in the almeState.history.
         */
        const initializeAlmeApp = async (): Promise<void> => {
            // Example setting config settings.
            await setAlmeConfigSettings(apiUtils);

            const curHistory = await loadAlmeHistory(apiUtils.current, almeState, setShouldDisplayTimeout);

            await handleLaunchQuestion(almeState, apiUtils, curHistory);

            let isLiveChatConnected = false;
            if (curHistory?.length) {
                const lastHistoryItem = curHistory[curHistory.length - 1] as LiveChatNotificationResponseNext;
                isLiveChatConnected = isLiveChatResponse(lastHistoryItem);
            }

            const chatResponses = curHistory.filter((value) => {
                return isLiveChatResponse(value) && typeof value?.extensionData?.MessageIndex === 'number';
            });
            let liveChatIndex = -1;

            if (chatResponses.length) {
                const lastChatResponse = chatResponses[chatResponses.length - 1] as LiveChatNotificationResponseNext;
                liveChatIndex = lastChatResponse.extensionData.MessageIndex;
            }

            if (shouldScrollOnNav) {
                scrollToResponse.current = true;
            }

            setAlmeStateWrapper({
                ...almeState,
                isOpen: true,
                history: curHistory,
                launchQuestion: undefined,
                isLiveChatConnected,
                liveChatIndex,
                launchData: undefined,
            } as AlmeAppState);

            if (almeSessionUtils?.current)
                almeSessionUtils?.current.setOnSessionTimeout();
        };

        if (isInitialized.current || !personaMapped || almeState.isMinimized || isIvaProLaunch?.current) {
            return;
        } else {
            isInitialized.current = true;
            void initializeAlmeApp();
        }
    }, [almeState, apiUtils, isInitialized, setAlmeStateWrapper, setShouldDisplayTimeout, personaMapped, isIvaProLaunch, almeSessionUtils, shouldScrollOnNav, scrollToResponse]);
}

async function setAlmeConfigSettings(apiUtils: React.MutableRefObject<AlmeApiUtils>): Promise<void> {
    try {
        const almeConfigs = await apiUtils.current.getAlmeConfiguration();
        if (almeConfigs) {
            const configSettings = almeConfigs.configurationSettings.reduce<Record<string, unknown>>((prev: Record<string, unknown>, x: ConfigurationKeyValuePair) => {
                return { ...prev, [x.Key]: x.Value };
            }, {});
            AlmeEnvironmentConfig.getInstance().setConfig(configSettings);
        }
    } catch (error) {
        defaultLogger.error(error);
    }
}

async function handleLaunchQuestion(almeState: AlmeAppState, apiUtils: React.MutableRefObject<AlmeApiUtils>, curHistory: AlmeResponseNext[]): Promise<void> {
    if (almeState.launchQuestion) {
        try {
            const questionResponse = await apiUtils.current.sendAlmeInput(almeState.launchQuestion);
            curHistory.push(questionResponse);
        } catch (ex) {
            defaultLogger.error(ex);
        }
    }
}
